import { AsyncState, HttpResult, Updater, loadedAsyncState, loadingAsyncState } from 'widgets-for-react'
import { ImageBase, ImageOptions } from '../../utils/image'
import { ParsedPhoneNumber } from '../../utils/phoneNumber'
import { SpaarneCentrumType, SpaarneDepartmentType } from '../../utils/spaarneIcons'
import { initialNotFoundState, NotFoundState } from '../notFound/notFoundState'
import { WaitingFor } from './blocks/block/waitingTimesBlock/waitingTimesBlockState'
import { BlocksState, SPItem } from './blocks/blocksState'
import { QuickUtilsState } from '../quickShareMenu/quickUtilitiesMenu'
import { SearchableAgendaEventPage, SearchableDossier, SearchableNewsPage, SearchablePatientFolder } from '../../backendApi/generated'
import { DossierNewsResult } from '../../backendApi/generated'

export type CmsPageState = {
  pageState: AsyncState<Page>
  notFoundState: NotFoundState
}

type PageBase = {
  blocks: BlocksState
  title: string
  intro: string
  url: string
  quickUtilsState?: QuickUtilsState
}

export type Page =
  | HomePage
  | CentrumPage
  | DepartmentPage
  | SubdepartmentPage
  | SpaarnePage
  | EventPage
  | NewsPage
  | LocationPage
  | SpecialistPage
  | WaitingTimesPage
  | ResearchPage
  | SubcentraPage
  | FriendPage
  | FolderPage
  | DossierPage

export type HomePage = PageBase & {
  kind: 'home'
}

export type Centrum = SPItem<SpaarneCentrumType> & {
  contactItems: ContactItem[]
  appointmentUrl: string
}

export type CentrumPage = PageBase & {
  kind: 'centrum'
  image?: ImageBase
  link?: Link
  centrum: Centrum
  childrenState: AsyncState<Link[]>
  videoUrl: AsyncState<string>
  menuOpened: boolean
  hasWaitingTimesState: AsyncState<boolean>
}

export type FriendPage = PageBase & {
  kind: 'friend' | 'friendHome'
  image?: ImageBase
  menuOpened: boolean
  showSidemenu: boolean
}

export type Department = SPItem<SpaarneDepartmentType> & {
  contactItems: ContactItem[]
  appointmentUrl: string
  centrum?: Centrum
}

export type DepartmentPage = PageBase & {
  kind: 'department'
  image?: ImageBase
  link?: Link
  department: Department
  hasWaitingTimesState: AsyncState<boolean>
  menuOpened: boolean
}

export type SubDepartment = SPItem & {
  contactItems: ContactItem[]
  appointmentUrl: string
  department?: Department
  centrum?: Centrum
  hideWaiting: boolean
}

export type SubdepartmentPage = PageBase & {
  kind: 'subdepartment'
  image?: ImageBase
  link?: Link
  subDepartment: SubDepartment
  hasWaitingTimesState: AsyncState<boolean>
}

export type SubCentra = {
  id: number
  title: string
  contactItems: ContactItem[]
  appointmentUrl: string
  centrum: Centrum
}

export type SubcentraPage = PageBase & {
  kind: 'subcentra'
  image?: ImageBase
  link?: Link
  subCentra: SubCentra
  childrenState: AsyncState<Link[]>
  menuOpened: boolean
  hasWaitingTimesState: AsyncState<boolean>
}

export type ParentPage = {
  url: string
  title: string
} & (
  | {
      kind: 'spaarne'
      parent?: ParentPage
    }
  | {
      kind: 'centrum'
      centrum?: Centrum
    }
)

export type SpaarnePage = PageBase & {
  kind: 'spaarne'
  image?: ImageBase
  link?: Link
  parent?: ParentPage
  childrenState: AsyncState<Link[]>
  menuOpened: boolean
}

export type EventPage = PageBase & {
  kind: 'event'
  image?: ImageBase
  link?: Link
  date: Date
  tags: string[]
  departments: string[]
  subDepartments: string[]
  centra: string[]
  specialists: string[]
  subcentra: string[]
  dossier: SearchableDossier | undefined
}

export type Author = {
  url?: string
  name: string
  job: string
  image?: ImageBase
}

export type NewsPage = PageBase & {
  kind: 'news'
  image?: ImageBase
  link?: Link
  date?: Date
  author?: Author
  tags: string[]
  departments: string[]
  subDepartments: string[]
  centra: string[]
  specialists: string[]
  subcentra: string[]
  dossier: SearchableDossier | undefined
}

export type Location = {
  address: string
  email: string
  id: number
  openingTimes: string
  openingTimesPolyclinic: string
  phoneNumber?: Array<ParsedPhoneNumber & { description: string }>
  postalAddress: string
  title: string
  visitingTimes: string
}

export type LocationPage = PageBase & {
  kind: 'location'
  image?: ImageBase
  link?: Link
  location?: Location
}

export type Specialist = {
  title: string
  academicTitle: string
  name: string
  tussenvoegsel: string
  surName: string
  role: string
  isDoctor: boolean
  areasOfFocus: string
  auxiliaryFunctions: string
  BIGRegistrationNumber: string
  study: string
  centra: SpaarneCentrumType[]
  departments: SpaarneDepartmentType[]
  subDepartments: string[]
}

export type SpecialistPage = PageBase & {
  kind: 'specialist'
  image?: ImageBase
  imageOptions?: ImageOptions
  specialist?: Specialist
}

export type WaitingTimesPage = PageBase & {
  kind: 'waitingTimes'
  image?: ImageBase
  link?: Link
  waitingFor?: WaitingFor
}

export type ResearchSpecialist = {
  name: string
  tussenvoegsel: string
  surName: string
  role: string
}

export type Research = {
  patientsNeeded: boolean
  specialists: ResearchSpecialist[]
  title: string
}

export type ResearchPage = PageBase & {
  kind: 'research'
  image?: ImageBase
  link?: Link
  research: Research
}

export type FolderContent = {
  htmlContent: string
  pdfUrl: string
  documentNumber: string
}

export type FolderPage = PageBase & {
  kind: 'folderPage'
  sharePointId: number
  folderContent: AsyncState<SearchablePatientFolder>
  lastModified: Date
  sideMenu: {
    isOpen: boolean
    activeHeaderId?: string
  }
}

export type DossierPage = PageBase & {
  kind: 'dossierPage'
  sharePointId: number
  dossierNews: AsyncState<DossierNewsResult>
  agendaPages: AsyncState<SearchableAgendaEventPage[]>
  lastModified: Date
  sideMenu: {
    isOpen: boolean
  }
  dossierTitle: string
  newsPage: number
  accumulatedNews: SearchableNewsPage[]
  highlightedNews?: SearchableNewsPage
  totalNewsCount: number
  childrenState: AsyncState<Link[]>
  menuOpened: boolean
}

export type Link = {
  url: string
  title: string
}

export type ContactItem = ({
  description: string
  telephone?: ParsedPhoneNumber
})

export const initialCmsPageState = (pageDataApi: () => Promise<HttpResult<Page>>): CmsPageState => ({
  pageState: loadingAsyncState(pageDataApi),
  notFoundState: initialNotFoundState,
})

export const cmsPageStateUpdaters = {
  pageStateUpdater:
    (updater: Updater<AsyncState<Page>>) =>
    (state: CmsPageState): CmsPageState => ({
      ...state,
      pageState: updater(state.pageState),
    }),
  notFoundStateUpdater:
    (updater: Updater<NotFoundState>) =>
    (state: CmsPageState): CmsPageState => ({
      ...state,
      notFoundState: updater(state.notFoundState),
    }),
  pageUpdater:
    (updater: Updater<Page>) =>
    (state: AsyncState<Page>): AsyncState<Page> =>
      state.kind === 'loaded' ? loadedAsyncState(updater(state.value)) : state,
  blocksUpdater:
    (updater: Updater<BlocksState>) =>
    (state: Page): Page => ({
      ...state,
      blocks: updater(state.blocks),
    }),
  pageTreeStateUpdater:
    (updater: Updater<AsyncState<Link[]>>) =>
    (state: Page): Page =>
      state.kind === 'spaarne' || state.kind === 'centrum' || state.kind === 'subcentra' || state.kind === 'dossierPage'
        ? {
            ...state,
            childrenState: updater(state.childrenState),
          }
        : state,
  pageVideoUrlStateUpdater:
    (updater: Updater<AsyncState<string>>) =>
    (state: Page): Page =>
      state.kind === 'centrum'
        ? {
            ...state,
            videoUrl: updater(state.videoUrl),
          }
        : state,
  menuOpenedUpdater: (state: Page): Page =>
    state.kind === 'spaarne' ||
    state.kind === 'friend' ||
    state.kind === 'friendHome' ||
    state.kind === 'department' ||
    state.kind === 'dossierPage'
      ? {
          ...state,
          menuOpened: !state.menuOpened,
        }
      : state,
  hasWaitingTimesPageStateUpdater:
    (updater: Updater<AsyncState<boolean>>) =>
    (state: Page): Page =>
      state.kind === 'department' ||
      state.kind === 'subdepartment' ||
      state.kind == 'subcentra' ||
      state.kind == 'centrum'
        ? {
            ...state,
            hasWaitingTimesState: updater(state.hasWaitingTimesState),
          }
        : state,
  folderContentUpdater:
    (updater: Updater<AsyncState<SearchablePatientFolder>>) =>
    (state: Page): Page =>
      state.kind === 'folderPage'
        ? {
            ...state,
            folderContent: updater(state.folderContent),
          }
        : state,
  dossierNewsUpdater:
    (updater: Updater<AsyncState<DossierNewsResult>>) =>
    (state: Page): Page =>
      state.kind === 'dossierPage'
        ? {
            ...state,
            dossierNews: updater(state.dossierNews),
          }
        : state,
  loadMoreDossierNewsUpdater:
    (fetchNews: (dossierTitle: string, page: number) => Promise<HttpResult<DossierNewsResult>>) =>
    (state: Page): Page =>
      state.kind === 'dossierPage' && state.dossierNews.kind === 'loaded'
        ? {
            ...state,
            accumulatedNews: [...state.accumulatedNews, ...(state.dossierNews.value.NewsPages ?? [])],
            highlightedNews: state.highlightedNews ?? state.dossierNews.value.HighlightedNewsPage ?? undefined,
            totalNewsCount: state.dossierNews.value.TotalCount,
            newsPage: state.newsPage + 1,
            dossierNews: loadingAsyncState(() => fetchNews(state.dossierTitle, state.newsPage + 1)),
          }
        : state,
  dossierAgendaUpdater:
    (updater: Updater<AsyncState<SearchableAgendaEventPage[]>>) =>
    (state: Page): Page =>
      state.kind === 'dossierPage'
        ? {
            ...state,
            agendaPages: updater(state.agendaPages),
          }
        : state,
  folderMenuToggleUpdater: (state: Page): Page =>
    state.kind === 'folderPage'
      ? {
          ...state,
          sideMenu: {
            ...state.sideMenu,
            isOpen: !state.sideMenu.isOpen,
          },
        }
      : state,
  folderMenuCloseUpdater: (state: Page): Page =>
    state.kind === 'folderPage'
      ? {
          ...state,
          sideMenu: {
            ...state.sideMenu,
            isOpen: false,
          },
        }
      : state,
  folderMenuActiveHeaderUpdater:
    (activeHeaderId?: string) =>
    (state: Page): Page =>
      state.kind === 'folderPage'
        ? {
            ...state,
            sideMenu: {
              ...state.sideMenu,
              activeHeaderId,
            },
          }
        : state,
  shareOptionsToggleUpdater: (state: Page): Page => {
    const newQuickUtilsState = state.quickUtilsState ? { ...state.quickUtilsState, isDropdownOpen: !state.quickUtilsState.isDropdownOpen } : undefined
    return {
      ...state,
      quickUtilsState: newQuickUtilsState,
    }
  },
  shareOptionsCloseUpdater: (state: Page): Page => {
    const newQuickUtilsState = state.quickUtilsState ? { ...state.quickUtilsState, isDropdownOpen: false, copyCompleted: false } : undefined
    return {
      ...state,
      quickUtilsState: newQuickUtilsState,
    }
  },
  onCopyUpdater: (state: Page): Page => {
    const newQuickUtilsState = state.quickUtilsState ? { ...state.quickUtilsState, copyCompleted: true } : undefined
    return {
      ...state,
      quickUtilsState: newQuickUtilsState,
    }
  }
}
